import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useHistory } from 'react-router-dom';

import IssuerEditer, { IssuerFormData } from '../../Components/Issuer/IssuerEditer';
import { CancelModal } from '../../Components/Common/Modal';
import { editIssuer } from '../../Utils/backStageApi';
import { checkAuth } from '../../Utils/common';

type Password = {
  text: string;
  visible: boolean;
  isError: boolean;
  errorMsg: string;
};

const CancelCreateModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const history = useHistory();
  return (
    <CancelModal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <Text textStyle="semibold_lg" color="text.main">
          有內容尚未儲存，確定離開？
        </Text>
      }
      confirmButtonName="離開"
      confirmButtonFunc={() => history.goBack()}
      cancelButtonName="繼續編輯"
    />
  );
};

const ConfirmCreateModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  password: Password;
  setPassword: React.Dispatch<React.SetStateAction<Password>>;
  createFunc: () => void;
}> = ({ isOpen, onClose, password, setPassword, createFunc }) => (
  <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
    <ModalOverlay />
    <ModalContent maxW="540px">
      <ModalHeader p="24px 24px 16px 24px">
        <Text textStyle="semibold_lg" color="text.main">
          請輸入密碼進行新增
        </Text>
      </ModalHeader>
      <ModalBody p="0 24px 16px 24px" color="grey.700">
        <FormControl w="80%" isInvalid={password.isError}>
          <InputGroup>
            <InputLeftElement color="text.main">
              <Text textStyle="semibold_sm">密碼</Text>
            </InputLeftElement>
            <Input
              type={password.visible ? 'text' : 'password'}
              placeholder="請輸入密碼"
              value={password.text}
              onChange={(e) => setPassword((prev) => ({ ...prev, text: e.target.value }))}
            />
            <InputRightElement
              cursor="pointer"
              onClick={() => setPassword((prev) => ({ ...prev, visible: !prev.visible }))}
            >
              {password.visible ? <ViewOffIcon color="primary.main" /> : <ViewIcon color="primary.main" />}
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{password.errorMsg}</FormErrorMessage>
        </FormControl>
      </ModalBody>
      <ModalFooter p="0 24px 24px 24px" gap="8px">
        <Button variant="secondary" onClick={onClose}>
          返回
        </Button>
        <Button onClick={createFunc} isDisabled={!password.text}>
          新增
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

const CreateIssuerPage: React.FC = () => {
  const history = useHistory();
  const toast = useToast();

  const [formData, setFormData] = useState<IssuerFormData | undefined>();
  const [rawDirty, setRawDirty] = useState(false);
  const [password, setPassword] = useState<Password>({
    text: '',
    visible: false,
    isError: false,
    errorMsg: '密碼錯誤',
  });

  const { isOpen: cancelIsOpen, onOpen: openCancel, onClose: closeCancel } = useDisclosure();
  const { isOpen: confirmIsOpen, onOpen: openConfirm, onClose: closeConfirm } = useDisclosure();

  useEffect(() => {
    checkAuth();
  }, []);

  // 只要 IssuerEditer 曾經回傳過任何值（含 undefined）就標記 dirty
  const handleSetFormData: React.Dispatch<React.SetStateAction<IssuerFormData | undefined>> = (val) => {
    setRawDirty(true);
    setFormData(val);
  };

  const isSubmittable = formData !== undefined;

  const handleCancel = () => {
    if (rawDirty) openCancel();
    else history.goBack();
  };

  const handleCloseConfirm = () => {
    setPassword({ text: '', visible: false, isError: false, errorMsg: '密碼錯誤' });
    closeConfirm();
  };

  const handleCreate = () => {
    setPassword((prev) => ({ ...prev, isError: false }));
    editIssuer(password.text, {
      name: formData!.name.trim(),
      acronym: formData!.acronym.trim(),
      cert_name: formData!.cert_name.trim(),
      ips: formData!.ips.trim(),
    })
      .then((response) => {
        if (response?.applied === true) {
          handleCloseConfirm();
          toast({ title: '機構已新增', status: 'success', duration: 5000, isClosable: true });
          history.goBack();
        } else {
          setPassword((prev) => ({
            ...prev,
            isError: true,
            errorMsg: response?.error_msg || '密碼錯誤',
          }));
        }
      })
      .catch(() => {
        setPassword((prev) => ({ ...prev, isError: true, errorMsg: '發生錯誤，請稍後再試' }));
      });
  };

  return (
    <Flex w="80vw" mx="auto" pt="50px" pb="100px" justify="center" alignItems="flex-start">
      <Flex maxW="679px" w="100%" direction="column">
        <Box>
          <Text fontWeight="700" fontSize="30px" lineHeight="133%" color="#000000" textAlign="center">
            新增機構
          </Text>
        </Box>
        <Box mt="75px">
          <IssuerEditer setFormData={handleSetFormData} />
        </Box>
        <Flex mt="60px" justify="center" gap="30px">
          <Button variant="outlinedCritical" p="8px 12px" border="1px solid" onClick={handleCancel}>
            取消
          </Button>
          <Button
            p="8px 12px"
            onClick={openConfirm}
            isDisabled={!isSubmittable}
            _disabled={{ opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' }}
            _hover={{ opacity: 0.85 }}
          >
            新增
          </Button>
        </Flex>
      </Flex>
      <CancelCreateModal isOpen={cancelIsOpen} onClose={closeCancel} />
      <ConfirmCreateModal
        isOpen={confirmIsOpen}
        onClose={handleCloseConfirm}
        password={password}
        setPassword={setPassword}
        createFunc={handleCreate}
      />
    </Flex>
  );
};

export default CreateIssuerPage;
